import { useMemo } from 'react';

import { getProxyAuthHeaders } from '@/auth/imsTokenStorage';
import { useImsToken } from '@/auth/UserIdContext';
import { config } from '@/config/env';

import { RestApi } from './RestApi.ts';

export class BridgewayApi extends RestApi {
	private imsToken: string | null = null;

	constructor(baseUrl: string, imsToken: string | null) {
		super(baseUrl);
		this.imsToken = imsToken;
	}

	private getAuthHeaders(): Record<string, string> {
		return getProxyAuthHeaders(this.imsToken);
	}

	public get = <ExpectedResponseType>(
		endpoint: string,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>('GET', endpoint, undefined, {
			...this.getAuthHeaders(),
			...additionalHeaders,
		});
	};

	public post = <ExpectedResponseType>(
		endpoint: string,
		body: object,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>('POST', endpoint, body, {
			...this.getAuthHeaders(),
			...additionalHeaders,
		});
	};

	public put = <ExpectedResponseType>(
		endpoint: string,
		body: object,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>('PUT', endpoint, body, {
			...this.getAuthHeaders(),
			...additionalHeaders,
		});
	};

	public patch = <ExpectedResponseType>(
		endpoint: string,
		body: object,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>('PATCH', endpoint, body, {
			...this.getAuthHeaders(),
			...additionalHeaders,
		});
	};

	public delete = <ExpectedResponseType>(
		endpoint: string,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>('DELETE', endpoint, undefined, {
			...this.getAuthHeaders(),
			...additionalHeaders,
		});
	};
}

export const useBridgewayApi = () => {
	const imsToken = useImsToken();
	return useMemo(
		() => new BridgewayApi(config.BRIDGEWAY_API_URL, imsToken),
		[imsToken],
	);
};
